import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { html } from '@polymer/polymer/lib/utils/html-tag.js';

export class KatapultFrame extends PolymerElement {
  static get template() {
    return html`
      <style include="flex">
        :host {
          display: block;
          position: relative;
          transition: opacity 0.3s;
          overflow: visible;
        }
        :host([_animate]) {
          transition:
            height 0.3s,
            width 0.3s,
            opacity 0.3s;
        }
        :host([overflow-hidden]) {
          overflow: hidden !important;
        }
        ::slotted(*) {
          position: absolute;
          top: 0;
          left: 0;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          @apply --katapult-frame-slotted-content;
        }
        ::slotted(:not(katapult-frame)) {
          transition: opacity 0.3s;
        }
        /* These two rules are important because they cause and element to stretch to fit it's container. */
        :host([direction='vertical']),
        :host([direction='vertical']) ::slotted(*) {
          min-width: -webkit-fill-available;
        }
        :host([direction='horizontal']),
        :host([direction='horizontal']) ::slotted(*) {
          min-height: -webkit-fill-available;
        }
      </style>
      <slot on-slotchange="_slotChangeHandler"></slot>
    `;
  }

  static get properties() {
    return {
      _animate: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      direction: {
        type: String,
        value: 'both',
        reflectToAttribute: true
      },
      _elementRecords: {
        type: Array,
        value: () => []
      },
      entries: {
        type: Object,
        value: () => ({})
      },
      if: {
        type: Boolean,
        value: false
      },
      _observedElements: {
        type: Array,
        value: () => []
      },
      log: {
        type: Boolean,
        value: false
      },
      overflowHidden: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      restamp: {
        type: Boolean,
        value: false
      },
      selected: {
        type: String
      },
      justify: {
        type: String,
        value: 'left',
        reflectToAttribute: true
      }
    };
  }

  static get observers() {
    return ['_updateStyle(_selectedRecord, direction)', '_updateSelectedRecord(_elementRecords, selected, if)'];
  }

  constructor() {
    super();
    this._observedElements = [];
  }

  connectedCallback() {
    super.connectedCallback();

    // Build resize observer and observe this element.
    this._resizeObserver ??= new ResizeObserver((entries) => {
      for (let entry of entries) {
        let elem = entry.target.parentElement;
        if (elem) {
          let record = elem._findElementRecord(entry.target);
          if (record) {
            record.resizeObserverEntry = entry;
            if (record == elem._selectedRecord) elem._updateStyle();
          }
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Disconnect resize observer.
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;
    this._observedElements = [];
  }

  ready() {
    super.ready();
    setTimeout(() => {
      this.skippedFirst = true;
    }, 300);
  }

  _findElementRecord(node) {
    if (node instanceof Element) {
      return this._elementRecords.find((record) => {
        if (node.nodeName == 'DOM-IF') return record.domIf == node;
        return record.elem == node || (node.__templatizeInstance != null && record.domIf?.__instance == node.__templatizeInstance);
      });
    } else if (typeof node == 'string') {
      return this._elementRecords.find((record) => record.key == node);
    }
  }

  _isHidden() {
    let elem = this;
    while (elem != null && elem != window) {
      if (elem.style.opacity === 0 || elem.style.opacity === '0') return true;
      elem = elem.offsetParent;
    }
    return false;
  }

  _observe(elem) {
    if (!this._observedElements.includes(elem)) {
      this._resizeObserver.observe(elem);
      this._observedElements.push(elem);
    }
  }

  async render(key) {
    // Try to find a record that matches this key.
    let record = this._findElementRecord(key);
    // If we find a record with a dom-if that has no instance yet, render it.
    if (record?.domIf != null && record.domIf.__instance == null) {
      await new Promise((res) => {
        record.domIf.addEventListener('dom-change', res, { once: true });
        record.domIf.if = true;
      });
      // Wait a sec for element to get put on this record.
      await new Promise((x) => setTimeout(x));
    }
    return record.elem;
  }

  _unobserve(elem) {
    let i = this._observedElements.indexOf(elem);
    if (i == -1) {
      this._resizeObserver.unobserve(elem);
      this._observedElements.splice(i, 1);
    }
  }

  _slotChangeHandler(e) {
    let nodes = Array.from(e.currentTarget.assignedNodes()).filter(
      (node) => node.nodeType == Node.ELEMENT_NODE && !['DOM-REPEAT'].includes(node.nodeName)
    );
    let temp = [];
    nodes.forEach((node) => {
      // Set flag if this is a dom-if element.
      let isDomIf = node.nodeName == 'DOM-IF';
      // Always force restamp on dom-if.
      // if (this.restamp && isDomIf || true) node.restamp = true;
      // Try to lookup an applicable record.
      let record = this._findElementRecord(node) ?? {};
      // Get key that this node should have on the record.
      let recordNodeKey = isDomIf ? 'domIf' : 'elem';
      // Force elem to be invisible.
      if (recordNodeKey == 'elem' && record.elem == null) {
        node.style.opacity = 0;
        node.style.pointerEvents = 'none';
      }
      // Make sure node exists on record.
      record[recordNodeKey] ??= node;
      // Setup record key.
      record.key ??= node.getAttribute('name');
      // Add record to new list of records.
      if (!temp.includes(record)) temp.push(record);
    });
    // Update element records.
    this._elementRecords = temp;
    // Loop through records and make sure all elements are being observed.
    this._elementRecords.forEach((record) => {
      if (record.elem) this._observe(record.elem);
    });
    // Loop through observed elements and remove any that no longer exist on records.
    this._observedElements.filter((elem) => this._findElementRecord(elem) == null).forEach((elem) => this._unobserve(elem));
  }

  _updateSelectedRecord() {
    let prevRecord = this._selectedRecord;
    // Find the new selected record;
    let newRecord = null;
    if (this.selected !== undefined) newRecord = this._findElementRecord(this.selected) ?? null;
    else if (this.if != null) {
      if (this.if) newRecord = this._elementRecords[0];
      else if (this._elementRecords[1] != null) newRecord = this._elementRecords[1];
    }
    if (this.log) console.log('prevRecord:', prevRecord, 'newRecord:', newRecord);
    // If the new record is different than the old one, hide the old one.
    if (newRecord != prevRecord) {
      // If the previous record exists and has a domIf.
      if (prevRecord?.domIf != null && prevRecord?.domIf.restamp) {
        // Clear any timeout callbacks.
        clearTimeout(prevRecord.timeout);
        // Setup a new timeout callback that will remove the element from the dom.
        prevRecord.timeout = setTimeout(() => {
          prevRecord.domIf.if = false;
          prevRecord.elem = null;
        }, 300);
      }
      if (newRecord) {
        // If the new record has a timeout, clear it.
        clearTimeout(newRecord.timeout);
        // If the new record has a domIf, make sure it is visible.
        if (newRecord.domIf != null) {
          newRecord.domIf.if = true;
        }
        // If the new record has an elem with a visible() method, call it.
        if (typeof newRecord?.elem?.visible === 'function') newRecord?.elem?.visible();
      }
      // Update animation flags.
      if (this.skippedFirst) {
        if (!this._isHidden()) {
          // Set _animate for 0.3s.
          if (this.log) console.log('Enabling animation');
          this._animate = true;
          window.clearTimeout(this._animationTimeout);
          this._animationTimeout = setTimeout(() => {
            if (this.log) console.log('Disabling animation');
            this._animate = false;
          }, 300);
        }
      } else {
        this.skippedFirst = true;
      }
      // Update the selected record.
      this._selectedRecord = newRecord;
    }
  }

  _updateStyle() {
    // Loop all entries and hide them.
    // let maxHeight = 0;
    // let maxWidth = 0;
    this._elementRecords.forEach((record) => {
      if (record.elem) {
        record.elem.style.opacity = 0;
        record.elem.style.pointerEvents = 'none';
      }
    });
    if (this._selectedRecord?.elem) {
      // Show the selected entry (if one exists).
      this._selectedRecord.elem.style.opacity = 1;
      this._selectedRecord.elem.style.pointerEvents = '';
      // setTimeout(() => {
      //   if (this.direction == 'vertical') this._selectedRecord.elem.style.minWidth = '-webkit-fill-available';
      //   if (this.direction == 'horizontal') this._selectedRecord.elem.style.minHeight = '-webkit-fill-available';
      // })
      if (this._selectedRecord?.resizeObserverEntry) {
        let height = (this.style.height = `${this._selectedRecord.resizeObserverEntry.borderBoxSize[0].blockSize ?? 0}px`);
        let width = (this.style.height = `${this._selectedRecord.resizeObserverEntry.borderBoxSize[0].inlineSize ?? 0}px`);
        // Update frame height.
        if (['vertical', 'both'].includes(this.direction)) {
          this.style.height = height;
        } else {
          this.style.height = '';
        }
        // Update frame width.
        if (['horizontal', 'both'].includes(this.direction)) {
          this.style.width = width;
        } else {
          this.style.width = '';
        }
      }
    } else {
      if (['vertical', 'both'].includes(this.direction)) this.style.height = '0px';
      else {
        this.style.height = '';
      }
      if (['horizontal', 'both'].includes(this.direction)) this.style.width = '0px';
      else {
        this.style.width = '';
      }
      this.style.minHeight = '';
      this.style.minWidth = '';
    }
  }
}
window.customElements.define('katapult-frame', KatapultFrame);
